import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';

import { batchAssignmentListFilters } from './batch-assignment.list-filters';
import { BATCH_ASSIGNMENT_LIST_PATH, BATCH_ASSIGNMENT_NEW_PATH, BATCH_ASSIGNMENT_TAB_IDS, BATCH_ASSIGNMENT_TABS, batchAssignmentPath } from './batch-assignment.search';

// Manifestos de Cessões (U23/U42). Leve: entra no `src/app/screens.ts` e no ⌘K. As ações com efeito do
// detalhe ficam presas à cessão da rota (padrão de `wallet-agent.ts`): a entrada é vazia e estrita, e a
// tela troca `run` e `preview` pelos do registro aberto. Fora do detalhe, o `run` recusa.

const READ = can('BatchAssignment', 'Read');

// eslint-disable-next-line lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33)
const OPEN_ASSIGNMENT_ONLY = 'This action only runs on the assignment detail screen, on the assignment that is open there.';
const needsOpenAssignment = () => {
  throw new Error(OPEN_ASSIGNMENT_ONLY);
};

// ─── lista ─────────────────────────────────────────────────────────────────────────────────────────

/** "Atualizar" (`BatchAssignmentButtonList.tsx:52-57`). A tela liga o `run` ao refetch. */
export const batchAssignmentRefresh = action({
  id: 'batchAssignment.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca a lista de cessões de novo. Não muda nada.`,
  run: () => undefined,
});

/** "Nova cessão" (`BatchAssignmentButtonList.tsx:71-75`): `BatchAssignment:Create`. */
export const batchAssignmentCreate = action({
  id: 'batchAssignment.create',
  kind: 'navigate',
  label: msg`Nova cessão`,
  description: msg`Abre o fluxo de Nova cessão: parâmetros, operações que atendem a eles e cálculo da cessão. Nada é gravado só por abrir.`,
  permission: can('BatchAssignment', 'Create'),
  run: ({ nav }) => nav.to(BATCH_ASSIGNMENT_NEW_PATH),
});

/** Clique na linha e o lápis da coluna "Ações" (`BatchAssignmentList.tsx:203-256`). */
export const batchAssignmentOpen = action({
  id: 'batchAssignment.open',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Abrir cessão`,
  description: msg`Abre uma cessão da lista: cabeçalho, ações do status e abas. Nada muda só por abrir.`,
  permission: READ,
  input: z.object({ id: z.string().min(1).max(64) }),
  run: ({ nav, input }) => nav.to(batchAssignmentPath(input.id)),
});

// eslint-disable-next-line lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33)
const LIST_ONLY = 'This action only opens a panel on the assignment list screen; the person fills it in and sends it.';
const needsList = () => {
  throw new Error(LIST_ONLY);
};

/**
 * "Incluir/Atualizar Cessão FGTS" (`BatchAssignmentButtonList.tsx:58-63`, tenant raiz). Só abre o formulário:
 * a cessão, as taxas e o envio ficam com a pessoa. A tela liga o `run` e só registra quando o botão aparece.
 */
export const batchAssignmentFgtsOpen = action({
  id: 'batchAssignment.fgts.open',
  kind: 'draft',
  label: msg`Incluir/Atualizar Cessão FGTS`,
  description: msg`Abre o formulário de Incluir/Atualizar Cessão FGTS na lista de cessões. Quem escolhe a cessão, preenche as taxas e envia é a pessoa.`,
  input: z.strictObject({}),
  run: needsList,
});

/** "Cessão por planilha" (`BatchAssignmentButtonList.tsx:64-69`, IS_KOVR + Create): abre o envio da planilha. */
export const batchAssignmentSpreadsheetOpen = action({
  id: 'batchAssignment.spreadsheet.open',
  kind: 'draft',
  label: msg`Cessão por planilha`,
  description: msg`Abre o painel de envio da planilha Excel da cessão, ou o andamento do processamento já iniciado. Quem escolhe o arquivo e processa é a pessoa.`,
  permission: can('BatchAssignment', 'Create'),
  input: z.strictObject({}),
  run: needsList,
});

/** "Enviar retorno R56 (Angá)" (`BatchAssignmentButtonList.tsx:76-81`, raiz + Update): abre o envio do R56. */
export const batchAssignmentR56Open = action({
  id: 'batchAssignment.r56.open',
  kind: 'draft',
  label: msg`Enviar retorno R56 (Angá)`,
  description: msg`Abre o painel de envio do arquivo R56 da Caixa para a Angá, ou o andamento do envio já iniciado. Quem escolhe o arquivo, decide se é ensaio e envia é a pessoa.`,
  permission: can('BatchAssignment', 'Update'),
  input: z.strictObject({}),
  run: needsList,
});

const filterKeys = batchAssignmentListFilters.keys.join(', ');

export const batchAssignmentFilter = filterAction('batchAssignment.filter', batchAssignmentListFilters, {
  description: msg`Filtra a lista de cessões. Filtro omitido é limpo; datas são civis (AAAA-MM-DD); valores em reais (1.000,00). Chaves: ${filterKeys}.`,
});

export const batchAssignmentListPage = listPageAction('batchAssignment.list.page');

export const batchAssignmentListScreen = defineScreen({
  id: 'batchAssignment.list',
  path: BATCH_ASSIGNMENT_LIST_PATH,
  title: msg`Cessões`,
  description: msg`Lista das cessões de operações de crédito a cessionários, com filtros, total das selecionadas, Nova cessão, Incluir/Atualizar Cessão FGTS, Cessão por planilha, Enviar retorno R56 (Angá) e as ações em lote (Excluir, Cancelar e Gerar Relatório).`,
  permission: READ,
  place: { icon: 'file-text', section: 'credit-note' },
  actions: [batchAssignmentRefresh, batchAssignmentFgtsOpen, batchAssignmentSpreadsheetOpen, batchAssignmentCreate, batchAssignmentR56Open, batchAssignmentOpen, batchAssignmentFilter, batchAssignmentListPage],
});

// ─── nova cessão ───────────────────────────────────────────────────────────────────────────────────

export const batchAssignmentNewScreen = defineScreen({
  id: 'batchAssignment.new',
  path: BATCH_ASSIGNMENT_NEW_PATH,
  title: msg`Nova cessão`,
  description: msg`Fluxo de Nova cessão: parâmetros (cessionário, datas, valores, status), as operações que atendem a eles para selecionar e o cálculo da cessão antes de gerá-la.`,
  permission: READ,
  place: { icon: 'file-text', parent: 'batchAssignment.list' },
  actions: [],
});

// ─── detalhe ───────────────────────────────────────────────────────────────────────────────────────

export const batchAssignmentDetailRefresh = action({
  id: 'batchAssignment.detail.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os dados da cessão aberta. Não muda nada.`,
  run: () => undefined,
});

const tabIds = BATCH_ASSIGNMENT_TAB_IDS.join(', ');

export const batchAssignmentTab = action<{ tab: (typeof BATCH_ASSIGNMENT_TAB_IDS)[number] }>({
  id: 'batchAssignment.detail.tab',
  kind: 'navigate',
  label: msg`Trocar de aba`,
  description: msg`Troca a aba da cessão aberta (Informações, Documentos, Operações, Parcelas ou Histórico). Abas: ${tabIds}.`,
  input: z.object({ tab: z.enum(BATCH_ASSIGNMENT_TAB_IDS) }),
  run: ({ nav, input }) => nav.search({ tab: input.tab === BATCH_ASSIGNMENT_TAB_IDS[0] ? undefined : input.tab, page: undefined, size: undefined }),
});

export const batchAssignmentDetailPage = listPageAction('batchAssignment.detail.page');

/** Abrir a operação da linha nas abas Operações e Parcelas (`OperationBatchAssigmentList.tsx:147`, `ParcelsBatchAssignmentList.tsx:106`). */
export const batchAssignmentOpenCreditNote = action({
  id: 'batchAssignment.openCreditNote',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Abrir operação`,
  description: msg`Abre a operação de crédito de uma linha das abas Operações ou Parcelas. Nada muda só por abrir.`,
  permission: can('CreditNote', 'Read'),
  input: z.object({ id: z.string().min(1).max(64) }),
  run: ({ nav, input }) => nav.to(`/ccb/operacoes/${encodeURIComponent(input.id)}`),
});

/** O cessionário da aba Informações (o link do `AutocompleteField`, InformationBatchAssignment.tsx:19-27). */
export const batchAssignmentOpenFund = action({
  id: 'batchAssignment.openFund',
  kind: 'navigate',
  page: true,
  label: msg`Abrir cessionário`,
  description: msg`Abre o cadastro do cessionário da cessão. Nada muda só por abrir.`,
  permission: can('Fund', 'Read'),
  input: z.object({ id: z.string().min(1).max(64) }),
  run: ({ nav, input }) => nav.to(`/cadastro/fundos/${encodeURIComponent(input.id)}`),
});

/** Entrada do "Iniciar cessão": as observações e o aceite (`StartBatchAssignmentSchema.ts:3-10`). */
export const batchAssignmentStartInput = z.strictObject({
  observations: z.string().max(2000).optional(),
  continueProcess: z.literal(true),
});

/**
 * "Iniciar cessão" (`ButtonOfActionsBatch.tsx:81-86`, só "Em Aberto"): `POST /BatchAssignment/{id}/Start`.
 * Presa à cessão aberta, como o legado (`useParams().id`).
 */
export const batchAssignmentStart = action({
  id: 'batchAssignment.start',
  kind: 'write',
  label: msg`Iniciar cessão`,
  description: msg`Inicia a cessão aberta na tela, só quando ela está Em Aberto. observations é opcional; continueProcess tem de ser true (a pessoa declara ciência de que as operações com status diferente de encerrado saem da cessão). A cessão é sempre a aberta e não pode ser escolhida. Pede a confirmação da pessoa.`,
  input: batchAssignmentStartInput,
  preview: () => ({ title: i18n._(msg`Iniciar cessão`), detail: i18n._(msg`Confirme os dados para iniciar a cessão.`) }),
  run: needsOpenAssignment,
});

/**
 * "Salvar documentos" (`ButtonOfActionsBatch.tsx:99-104`, ação `UploadDocs`): `PUT /BatchAssignment/{id}/upload`
 * com a lista da aba Documentos. Presa à cessão aberta; o agente não envia arquivo, só grava o que a pessoa juntou.
 */
export const batchAssignmentSaveDocuments = action({
  id: 'batchAssignment.saveDocuments',
  kind: 'write',
  label: msg`Salvar documentos`,
  description: msg`Grava na cessão aberta a lista da aba Documentos como está agora, com os documentos que a pessoa adicionou e ainda não salvou. Não recebe entrada nem envia arquivo: a cessão é sempre a aberta. Pede a confirmação da pessoa.`,
  input: z.strictObject({}),
  preview: () => ({ title: i18n._(msg`Salvar documentos`), detail: i18n._(msg`A lista de documentos da cessão será gravada como está na aba Documentos.`) }),
  run: needsOpenAssignment,
});

/** "Cancelar" (`ButtonOfActionsBatch.tsx:190-195`, tenant raiz): `PUT /BatchAssignment/{id}/cancel`. */
export const batchAssignmentCancel = action({
  id: 'batchAssignment.cancel',
  kind: 'destructive',
  label: msg`Cancelar`,
  description: msg`Cancela a cessão aberta na tela. Não recebe entrada: a cessão é sempre a aberta. Pede a confirmação da pessoa.`,
  input: z.strictObject({}),
  preview: () => ({ title: i18n._(msg`Cancelar cessão`), detail: i18n._(msg`Tem certeza que deseja cancelar essa cessão?`), tone: 'destructive' }),
  run: needsOpenAssignment,
});

/** "Excluir" (`ButtonOfActionsBatch.tsx:196-201`, tenant raiz): `DELETE /BatchAssignment/{id}`. */
export const batchAssignmentRemove = action({
  id: 'batchAssignment.remove',
  kind: 'destructive',
  label: msg`Excluir`,
  description: msg`Exclui a cessão aberta na tela e volta para a lista. Não recebe entrada: a cessão é sempre a aberta. Pede a confirmação da pessoa.`,
  input: z.strictObject({}),
  preview: () => ({ title: i18n._(msg`Excluir cessão`), detail: i18n._(msg`Tem certeza que deseja excluir essa cessão?`), tone: 'destructive' }),
  run: needsOpenAssignment,
});

export const batchAssignmentDetailScreen = defineScreen({
  id: 'batchAssignment.detail',
  path: `${BATCH_ASSIGNMENT_LIST_PATH}/$batchAssignmentId`,
  title: msg`Cessão`,
  description: msg`Ficha de uma cessão: número, cessionário, etapas do status e as ações conforme o status; abas Informações, Documentos (adicionar, excluir e salvar documentos), Operações (aprovar liquidação, remover da cessão), Parcelas e Histórico.`,
  permission: READ,
  place: { icon: 'file-text', parent: 'batchAssignment.list', record: msg`Cessão`, tabs: BATCH_ASSIGNMENT_TABS },
  actions: [batchAssignmentDetailRefresh, batchAssignmentTab, batchAssignmentDetailPage, batchAssignmentOpenCreditNote, batchAssignmentOpenFund, batchAssignmentStart, batchAssignmentSaveDocuments, batchAssignmentCancel, batchAssignmentRemove],
});
